/**
 * Owner-admin CSRF token plumbing for the SPA.
 *
 * Background: every mutating /api/admin/* route is gated by
 * `requireOwnerAdminCsrf` server-side, which requires a matching
 * `X-CSRF-Token` header (HMAC-derived from the session cookie). This
 * module is the single source of truth for that token in the browser.
 *
 * Sources, in priority order:
 *   1. Fresh `GET /api/admin/me` body (used immediately before mutations).
 *   2. Login / AdminGuard response body stashed in memory.
 *   3. The non-httpOnly `owner_admin_csrf` cookie as a last resort.
 */

const CSRF_COOKIE = "owner_admin_csrf";

let cachedToken: string | null = null;

function readTokenCookie(): string | null {
  if (typeof document === "undefined") return null;
  const parts = document.cookie.split(";");
  for (const part of parts) {
    const [k, ...rest] = part.trim().split("=");
    if (k === CSRF_COOKIE) return decodeURIComponent(rest.join("="));
  }
  return null;
}

export function setAdminCsrfToken(token: string | null | undefined): void {
  cachedToken = typeof token === "string" && token.length > 0 ? token : null;
}

export function getAdminCsrfToken(): string | null {
  if (cachedToken) return cachedToken;
  const fromCookie = readTokenCookie();
  if (fromCookie) {
    cachedToken = fromCookie;
    return fromCookie;
  }
  return null;
}

export function adminCsrfHeaders(): Record<string, string> {
  const token = getAdminCsrfToken();
  return token ? { "X-CSRF-Token": token } : {};
}

/**
 * Ask the server for the CSRF token that matches the current session
 * cookie. Always prefer this over a cached/cookie value before mutating.
 */
export async function refreshAdminCsrfToken(): Promise<string | null> {
  try {
    const me = await fetch("/api/admin/me", { credentials: "include" });
    const data = (await me.json().catch(() => ({}))) as {
      authenticated?: boolean;
      csrfToken?: string;
    };
    if (data.authenticated && typeof data.csrfToken === "string" && data.csrfToken.length > 0) {
      setAdminCsrfToken(data.csrfToken);
      return data.csrfToken;
    }
  } catch {
    /* fall through */
  }
  return getAdminCsrfToken();
}

/**
 * Thin wrapper around `fetch` that automatically attaches credentials and
 * a freshly refreshed CSRF header for every mutating verb.
 */
export async function adminFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const isMutating =
    method === "POST" ||
    method === "PUT" ||
    method === "PATCH" ||
    method === "DELETE";
  const headers = new Headers(init.headers ?? {});
  if (isMutating && !headers.has("X-CSRF-Token")) {
    const token = await refreshAdminCsrfToken();
    if (token) headers.set("X-CSRF-Token", token);
  }
  const res = await fetch(input, { ...init, credentials: "include", headers });
  if (!isMutating || res.status !== 403) return res;

  let bodyPreview = "";
  try {
    bodyPreview = await res.clone().text();
  } catch {
    return res;
  }
  if (!/CSRF token (invalid|missing)/i.test(bodyPreview)) return res;

  // Second chance: remint and retry with the /me body token directly
  // (do not re-read a possibly-stale document.cookie).
  const fresh = await refreshAdminCsrfToken();
  if (!fresh) return res;

  const retryHeaders = new Headers(init.headers ?? {});
  retryHeaders.set("X-CSRF-Token", fresh);
  return fetch(input, { ...init, credentials: "include", headers: retryHeaders });
}
